import { useRef, useState } from "react";
import {
  FileUp,
  ClipboardPaste,
  CheckCircle2,
  X,
  Lock,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation";

/**
 * Optional context panel on Step 1.
 * - Upload a PDF or .pptx (parsed server-side, file is never stored)
 * - Or paste long-form text
 * Once loaded, the parsed text travels in browser state and is sent to Claude on each turn.
 */
export default function ContextPanel({ context, sourceLabel, onSet, onClear }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("file"); // "file" | "paste"
  const [pasteDraft, setPasteDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const hasContext = !!(context && context.trim());

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large", {
        description: `Max is 10 MB — yours is ${(file.size / 1024 / 1024).toFixed(1)} MB.`,
      });
      return;
    }

    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".pdf") && !lower.endsWith(".pptx")) {
      toast.error("Only PDF or .pptx, please", {
        description: "Old .ppt and other formats aren't supported. Save as .pptx.",
      });
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/awake/context/extract`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      onSet({
        text: data.extracted_text,
        label: data.source_label,
        wordCount: data.word_count,
      });
      const wasTrimmed = (data.source_label || "").includes("trimmed");
      toast.success("Context added", {
        description: wasTrimmed
          ? `${data.word_count.toLocaleString()} words from ${file.name} (trimmed for length)`
          : `${data.word_count.toLocaleString()} words from ${file.name}`,
      });
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Couldn't read this file.";
      toast.error("Couldn't read this file", { description: msg });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handlePasteSave = () => {
    const text = pasteDraft.trim();
    if (text.length < 40) {
      toast.error("That's a bit thin", {
        description: "Paste at least a paragraph for it to be useful.",
      });
      return;
    }
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    onSet({
      text,
      label: `pasted text · ${wordCount.toLocaleString()} words`,
      wordCount,
    });
    toast.success("Context added", {
      description: `${wordCount.toLocaleString()} words pasted.`,
    });
    setPasteDraft("");
    setOpen(false);
  };

  // ----- LOADED state -----
  if (hasContext) {
    const wc = (context.match(/\S+/g) || []).length;
    return (
      <div className="weave-context-loaded" data-testid="context-loaded">
        <div className="flex items-start gap-3">
          <CheckCircle2
            size={18}
            style={{ color: "#3c8386", flexShrink: 0, marginTop: 2 }}
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500 mb-1">
              Context loaded
            </div>
            <div
              className="text-sm text-slate-800 truncate"
              title={sourceLabel}
              data-testid="context-source-label"
            >
              {sourceLabel || `${wc.toLocaleString()} words`}
            </div>
            <div className="text-[12px] text-slate-500 mt-1">
              Your Trusted Peer will read this before answering.
            </div>
          </div>
          <button
            type="button"
            onClick={onClear}
            className="weave-context-clear"
            aria-label="Remove context"
            title="Remove context"
            data-testid="context-clear-btn"
          >
            <X size={14} />
            Clear
          </button>
        </div>
      </div>
    );
  }

  // ----- COLLAPSED state -----
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="weave-context-toggle"
        data-testid="context-open-btn"
      >
        <FileUp size={14} />
        <span className="weave-context-toggle-label">Add context</span>
        <span className="weave-context-toggle-hint">
          Got a pitch deck or business plan? Your Trusted Peer will read it before answering.
        </span>
        <ChevronDown size={14} style={{ marginLeft: "auto", color: "#94a3b8" }} />
      </button>
    );
  }

  // ----- EXPANDED state -----
  return (
    <div className="weave-context-panel" data-testid="context-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500">
          Add context (optional)
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-slate-400 hover:text-slate-600 p-1"
          aria-label="Close"
          data-testid="context-close-btn"
        >
          <ChevronUp size={16} />
        </button>
      </div>

      <p className="text-sm text-slate-600 mb-4 leading-relaxed">
        Pitch deck, business plan, brief, notes — anything that gives your Trusted Peer
        the context they're missing.
      </p>

      <div className="weave-context-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "file"}
          className={mode === "file" ? "is-active" : ""}
          onClick={() => setMode("file")}
          data-testid="context-tab-file"
        >
          <FileUp size={13} /> Upload
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "paste"}
          className={mode === "paste" ? "is-active" : ""}
          onClick={() => setMode("paste")}
          data-testid="context-tab-paste"
        >
          <ClipboardPaste size={13} /> Paste text
        </button>
      </div>

      {mode === "file" && (
        <div className="weave-context-file-zone">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={uploading}
            className="hidden"
            data-testid="context-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="weave-context-file-btn"
            data-testid="context-file-pick-btn"
          >
            {uploading ? (
              <>
                <span className="weave-spinner" />
                Reading your file…
              </>
            ) : (
              <>
                <FileUp size={16} />
                Choose a PDF or .pptx
              </>
            )}
          </button>
          <p className="text-xs text-slate-400 mt-3">
            PDF or .pptx, up to 10 MB. Old .ppt isn't supported.
          </p>
        </div>
      )}

      {mode === "paste" && (
        <div>
          <textarea
            className="weave-input"
            rows={6}
            placeholder="Paste your pitch deck text, business plan, brief, or relevant notes…"
            value={pasteDraft}
            onChange={(e) => setPasteDraft(e.target.value)}
            disabled={uploading}
            data-testid="context-paste-input"
          />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-slate-400">
              {(pasteDraft.match(/\S+/g) || []).length.toLocaleString()} words
            </span>
            <button
              type="button"
              onClick={handlePasteSave}
              className="weave-btn-primary"
              disabled={pasteDraft.trim().length < 40}
              data-testid="context-paste-save-btn"
            >
              Save context
            </button>
          </div>
        </div>
      )}

      <div className="weave-context-privacy">
        <Lock size={12} aria-hidden="true" />
        <span>
          Your file is read once on the server and discarded immediately. The extracted
          text stays in your browser session — sent to Claude only during AWAKE turns,
          never stored.
        </span>
      </div>

      {mode === "file" && (
        <div className="weave-context-tip">
          <AlertCircle size={12} aria-hidden="true" />
          <span>
            Scanned PDFs (image-only) won't work — paste the text instead.
          </span>
        </div>
      )}
    </div>
  );
}
